import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { StatusBadge } from '../../components/ui/Badge.js';
import { Card } from '../../components/ui/Card.js';
import { api } from '../../services/api.js';

/** The collected identity-document sides (312: eight — no student card back), in display order. */
const SIDES: { key: string; label: string }[] = [
  { key: 'ic_front', label: 'NRIC front' },
  { key: 'ic_back', label: 'NRIC back' },
  { key: 'driving_licence_front', label: 'Driving licence front' },
  { key: 'driving_licence_back', label: 'Driving licence back' },
  { key: 'passport_front', label: 'Passport front' },
  { key: 'passport_back', label: 'Passport back' },
  { key: 'selfie', label: 'Selfie' },
  { key: 'student_card_front', label: 'Student card front' },
  // 312 (#538): no student card BACK — it can carry ATM card details, and is no longer collected.
];

interface IdentitySummary {
  status: 'pending' | 'submitted' | 'approved' | 'rejected' | 'resubmitted';
  rejection_reason?: string;
  sides: Record<string, boolean>;
}

export function IdentityDocuments() {
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ['identity'],
    queryFn: () => api.get<IdentitySummary>('/users/me/identity'),
  });
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const status = data?.status ?? 'pending';
  const sides = data?.sides ?? {};

  async function handleUpload(side: string, e: React.ChangeEvent<HTMLInputElement>) {
    // Capture the input element before any `await` — React nulls out the
    // synthetic event's `currentTarget` once the handler yields, so reading
    // it in `finally` after an async gap throws (React 19 event pooling).
    const input = e.currentTarget;
    const file = input.files?.[0];
    if (!file) return;
    setMsg(null);
    setBusy(side);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.upload(`/users/me/identity/${side}`, fd);
      await queryClient.invalidateQueries({ queryKey: ['identity'] });
      setMsg({ type: 'success', text: 'Document uploaded.' });
    } catch (err) {
      const text = err instanceof Error ? err.message : 'Upload failed';
      setMsg({ type: 'error', text });
    } finally {
      setBusy(null);
      input.value = '';
    }
  }

  return (
    <Card>
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-ink">Identity documents</h2>
        <StatusBadge status={status} />
      </div>

      {status === 'rejected' && data?.rejection_reason && (
        <p className="mb-4 text-sm text-red-600">Rejected: {data.rejection_reason}</p>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {SIDES.map(({ key, label }) => (
          <div
            key={key}
            className="flex items-center justify-between gap-2 rounded-xl border border-hairline shadow-[0px_1px_2px_0px_rgba(0,30,43,0.04)] p-3"
          >
            <div>
              <p className="text-sm text-ink">{label}</p>
              <p className="text-xs text-steel">{sides[key] ? 'Provided' : 'Not provided'}</p>
            </div>
            <div className="flex items-center gap-3">
              {sides[key] && (
                <button
                  type="button"
                  className="text-xs font-medium text-steel hover:underline"
                  onClick={async () => {
                    const url = await api.getBlobUrl(`/users/me/identity/${key}/file`);
                    window.open(url, '_blank', 'noopener');
                    // Release the object URL once the new tab has had time to load it.
                    setTimeout(() => URL.revokeObjectURL(url), 60_000);
                  }}
                >
                  View
                </button>
              )}
              <label className="cursor-pointer text-xs font-medium text-brand-blue hover:underline">
                {sides[key] ? 'Replace' : 'Upload'}
                <input
                  type="file"
                  data-side={key}
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  className="sr-only"
                  disabled={busy !== null}
                  onChange={(e) => handleUpload(key, e)}
                />
              </label>
            </div>
          </div>
        ))}
      </div>

      {msg && (
        <p className={`mt-3 text-xs ${msg.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
          {msg.text}
        </p>
      )}
    </Card>
  );
}
